import { useMemo, useState } from "react";
import { useVehicle } from "../context/VehicleContext";
import { createSession } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Scale, Save, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };

export default function CornerBalance() {
  const { vehicleLabel } = useVehicle();
  const [unit, setUnit] = useState("lb");
  const [w, setW] = useState({ FL: "", FR: "", RL: "", RR: "" });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setW((p) => ({ ...p, [k]: v.replace(/[^0-9.]/g, "") }));

  const r = useMemo(() => {
    const FL = num(w.FL), FR = num(w.FR), RL = num(w.RL), RR = num(w.RR);
    const total = FL + FR + RL + RR;
    const pct = (x) => (total ? (x / total) * 100 : 0);
    const cross = total ? ((FR + RL) / total) * 100 : 0; // RF+LR diagonal
    return {
      total, FL, FR, RL, RR,
      front: FL + FR, rear: RL + RR, left: FL + RL, right: FR + RR,
      frontPct: pct(FL + FR), rearPct: pct(RL + RR), leftPct: pct(FL + RL), rightPct: pct(FR + RR),
      cross, crossDelta: Math.abs(cross - 50),
    };
  }, [w]);

  const filled = r.total > 0;
  const crossColor = r.crossDelta <= 0.5 ? "#10B981" : r.crossDelta <= 1.5 ? "#F59E0B" : "#EF4444";
  const crossStatus = r.crossDelta <= 0.5 ? "BALANCED" : r.crossDelta <= 1.5 ? "CLOSE" : "OFF BALANCE";

  const reset = () => setW({ FL: "", FR: "", RL: "", RR: "" });

  const save = async () => {
    if (!filled) { toast.error("Enter the four corner weights first"); return; }
    setSaving(true);
    try {
      await createSession({
        title: `Corner Balance — ${vehicleLabel || "Vehicle"}`,
        notes: `Cross-weight ${r.cross.toFixed(1)}% · total ${r.total.toFixed(0)} ${unit}`,
        results: {
          cornerBalance: {
            unit, weights: { FL: r.FL, FR: r.FR, RL: r.RL, RR: r.RR },
            total: r.total, front: r.front, rear: r.rear, left: r.left, right: r.right,
            crossWeightPct: +r.cross.toFixed(2),
          },
        },
      });
      toast.success("Saved to session history");
    } catch { toast.error("Could not save — try again"); }
    finally { setSaving(false); }
  };

  const Cell = ({ k }) => (
    <div className="rounded-md bg-surface border border-steel p-4">
      <div className="font-mono text-[10px] text-slate-400 tracking-widest uppercase mb-2">{k}</div>
      <div className="flex items-baseline gap-2">
        <Input data-testid={`cb-input-${k}`} inputMode="decimal" value={w[k]} onChange={(e) => set(k, e.target.value)}
          placeholder="0" className="bg-ink border-steel text-white font-mono text-xl tabular-nums h-12" />
        <span className="font-mono text-xs text-slate-500">{unit}</span>
      </div>
    </div>
  );

  const Stat = ({ label, value, pct, testId }) => (
    <div className="rounded-md bg-ink/50 border border-steel p-3 text-center" data-testid={testId}>
      <div className="font-mono text-[9px] text-slate-500 tracking-widest uppercase">{label}</div>
      <div className="font-mono font-bold text-lg tabular-nums text-white mt-1">{filled ? value.toFixed(0) : "--"}</div>
      {pct != null && <div className="font-mono text-[10px] text-cyan tabular-nums">{filled ? pct.toFixed(1) : "--"}%</div>}
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="corner-balance-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-lg bg-cyan/10 border border-cyan/20 flex items-center justify-center text-cyan"><Scale size={22} /></div>
          <div>
            <h1 className="font-heading font-bold tracking-tight text-2xl">Corner Balance</h1>
            <p className="font-mono text-[11px] text-slate-500 tracking-widest uppercase">{vehicleLabel || "No vehicle"} · cross-weight calculator</p>
          </div>
        </div>
        <div className="flex gap-1 bg-elevated rounded-lg p-1 border border-steel">
          {["lb", "kg"].map((u) => (
            <button key={u} data-testid={`cb-unit-${u}`} onClick={() => setUnit(u)}
              className={`px-3 py-1.5 rounded-md font-mono text-xs uppercase tracking-widest transition-all ${unit === u ? "bg-cyan text-ink font-bold" : "text-slate-400"}`}>{u}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 max-w-md mx-auto">
        <Cell k="FL" /><Cell k="FR" />
        <Cell k="RL" /><Cell k="RR" />
      </div>

      <div className="mt-6 rounded-md bg-surface border border-steel p-6">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <Stat label="Total" value={r.total} testId="cb-total" />
          <Stat label="Front" value={r.front} pct={r.frontPct} testId="cb-front" />
          <Stat label="Rear" value={r.rear} pct={r.rearPct} testId="cb-rear" />
          <Stat label="Left" value={r.left} pct={r.leftPct} testId="cb-left" />
          <Stat label="Right" value={r.right} pct={r.rightPct} testId="cb-right" />
        </div>

        <div className="mt-5 rounded-md border p-5 text-center" style={{ borderColor: `${crossColor}55`, background: `${crossColor}11` }} data-testid="cb-cross">
          <div className="font-mono text-[10px] text-slate-400 tracking-widest uppercase">Cross-weight (RF + LR diagonal)</div>
          <div className="font-mono font-black text-4xl tabular-nums mt-1" style={{ color: crossColor }}>
            {filled ? `${r.cross.toFixed(1)}%` : "--"}
          </div>
          <div className="font-mono text-[11px] tracking-widest uppercase mt-1" style={{ color: crossColor }}>
            {filled ? `${crossStatus} · target 50.0%` : "Enter weights"}
          </div>
        </div>
      </div>

      <div className="mt-5 flex gap-2 justify-center">
        <Button data-testid="cb-reset" onClick={reset} variant="outline" className="bg-elevated border-steel text-slate-300 hover:bg-steel font-mono text-xs uppercase tracking-widest">
          <RotateCcw size={14} /> Reset
        </Button>
        <Button data-testid="cb-save" onClick={save} disabled={saving || !filled} className="bg-pass text-ink hover:bg-pass/80 font-mono text-xs uppercase tracking-widest font-bold disabled:opacity-40">
          <Save size={14} /> {saving ? "Saving…" : "Save to History"}
        </Button>
      </div>
      <p className="mt-4 text-center font-mono text-[10px] text-slate-600 leading-relaxed max-w-lg mx-auto">
        Put each wheel on a scale pad on a flat, level surface with the driver and fuel load you race with. 50% cross-weight = neutral handling.
      </p>
    </div>
  );
}
